import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import {
 HelpArticle, HelpConditionContext, HelpTag, InfoMessageCollection
} from '../models';

/**
 * Abstract base class for the Help Service.
 * Each portal (public-portal, admin-portal) should extend this class
 * and provide their own implementation of getConditionContext() and getArticleDefinitions().
 */
@Injectable()
export abstract class HelpServiceBase {
    public articles: HelpArticle[] = [];

    public infoMessages: InfoMessageCollection = {};

    constructor(protected sanitizer: DomSanitizer) {}

    /**
     * Initialize the help articles and info messages. Call this in the constructor
     * of the derived class after all dependencies are available.
     */
    protected initializeArticles(): void {
        const context = this.getConditionContext();
        const articleDefinitions = this.getArticleDefinitions();

        this.articles = articleDefinitions
            // Filter out any articles based on conditionals
            .filter(article => article.checkConditions(context))
            // Process the remaining articles
            .map(article => {
                article.applyInlineConditions(context);
                article.replaceIcons();
                article.trustedBody = this.sanitizer.bypassSecurityTrustHtml(article.body);
                return article;
            });

        // Initialize info messages
        this.infoMessages = this.getInfoMessageDefinitions();
    }

    /**
     * Get the condition context for filtering and processing articles.
     * Derived classes must implement this to provide portal-specific context.
     */
    protected abstract getConditionContext(): HelpConditionContext;

    /**
     * Get the article definitions for this portal.
     * Derived classes must implement this to provide portal-specific articles.
     */
    protected abstract getArticleDefinitions(): HelpArticle[];

    /**
     * Get the info message definitions for this portal.
     * Derived classes can override this to provide portal-specific info messages.
     * Info messages are displayed in dialogs when users click info icons,
     * and are separate from the help articles shown in the help sidebar.
     */
    protected getInfoMessageDefinitions(): InfoMessageCollection {
        return {};
    }

    /**
     * Determine which help tag to display based on the current URL.
     * Derived classes must implement this to provide portal-specific URL patterns.
     * @param currentUrl The current URL path
     * @returns The tag that matches the current page, or a default tag
     */
    public abstract determineTagByUrl(currentUrl: string): HelpTag;

    /**
     * Get all available help tags for this portal.
     * Derived classes must implement this to provide portal-specific tags.
     * @returns Array of all help tags available in this portal
     */
    public abstract getAvailableTags(): HelpTag[];
}
